import { parseBooleanVal } from "Utils/utils";

export const resolveMandatoryFlag = (value) => {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0" || value == null) {
    return false;
  }
  return parseBooleanVal(value);
};

export const getParticipantTaskList = (participant) => {
  const tasks =
    participant?.participant_tasks ??
    participant?.participantTasks ??
    participant?.tasks ??
    [];
  if (Array.isArray(tasks)) return tasks;
  if (typeof tasks?.toJS === "function") return tasks.toJS();
  if (typeof tasks?.toArray === "function") return tasks.toArray();
  return [];
};

export const readMandatoryFromTask = (taskItem) =>
  resolveMandatoryFlag(
    taskItem?.is_mandatory ??
      taskItem?.isMandatory ??
      taskItem?.is_mandatory_task
  );

export const resolveParticipantsForShiftTasks = (
  selectedParticipant,
  participantOptions
) => {
  if (!selectedParticipant) return [];

  const resolveOne = (item) => {
    if (!item) return null;

    const uuid =
      item?.uuid ?? item?.value ?? (typeof item === "string" ? item : null);
    const fromOptions = uuid
      ? participantOptions?.find(
          (p) => p?.uuid === uuid || p?.value === uuid
        )
      : null;

    return fromOptions || (typeof item === "object" && item?.id ? item : null);
  };

  if (Array.isArray(selectedParticipant)) {
    return selectedParticipant.map(resolveOne).filter(Boolean);
  }

  const resolved = resolveOne(selectedParticipant);
  return resolved ? [resolved] : [];
};

export const mapParticipantTasksToShiftTasks = (participants) => {
  if (!Array.isArray(participants)) return [];

  const shiftTasks = [];

  participants.forEach((participant) => {
    const participantTasks = getParticipantTaskList(participant);

    if (!participant?.id || !Array.isArray(participantTasks)) return;

    participantTasks.forEach((taskItem) => {
      const taskText =
        typeof taskItem?.task === "string"
          ? taskItem.task.trim()
          : String(taskItem?.description ?? taskItem?.task ?? "").trim();

      if (!taskText) return;

      shiftTasks.push({
        task: taskText,
        is_mandatory: readMandatoryFromTask(taskItem),
        participant_task_id:
          taskItem?.id ?? taskItem?.participant_task_id ?? null,
        pt_id: participant.id,
        assign_to: "all",
        worker_id: null,
      });
    });
  });

  return shiftTasks;
};

export const getParticipantSelectionKey = (ptId) => {
  if (ptId == null || ptId === "") return "";

  const values = Array.isArray(ptId) ? ptId : [ptId];

  return values
    .map((item) => item?.uuid ?? item?.value ?? item)
    .filter(Boolean)
    .map(String)
    .sort()
    .join("|");
};

export const EMPTY_SHIFT_TASK = {
  task: "",
  is_mandatory: false,
  assign_to: "all",
  worker_id: null,
};

const isUnassignedUnsavedShiftTask = (task) =>
  task &&
  (task.pt_id == null || task.pt_id === "") &&
  !task.id &&
  !(Array.isArray(task.ids) && task.ids.length);

/**
 * Advance create only: attach a participant id to the default blank row
 * (no pt_id, not yet saved). Does not rewrite existing/saved tasks.
 */
export const stampUnassignedTasksWithParticipant = (tasks = [], ptId) => {
  const numericPtId =
    ptId != null && ptId !== "" ? Number(ptId) : NaN;
  if (!Number.isFinite(numericPtId) || numericPtId <= 0) return tasks;

  let changed = false;
  const next = (Array.isArray(tasks) ? tasks : []).map((task) => {
    if (!isUnassignedUnsavedShiftTask(task)) return task;
    changed = true;
    return { ...task, pt_id: numericPtId };
  });
  return changed ? next : tasks;
};

/**
 * Build / sync editable note rows for the given participants (basic + advance).
 * Preserves existing note text when the same pt_id remains selected.
 */
export const syncParticipantNoteRows = (participants = [], previousNotes = []) => {
  const prev = Array.isArray(previousNotes) ? previousNotes : [];
  const list = Array.isArray(participants) ? participants.filter(Boolean) : [];

  // Advance show may return a single shift-level `note` without pt_id — keep it on first row.
  const orphanText = prev.find(
    (note) =>
      (note?.pt_id == null || note?.pt_id === "") &&
      typeof note?.notes === "string" &&
      note.notes.trim()
  )?.notes;

  return list.map((participant, index) => {
    const ptId = participant?.id != null ? Number(participant.id) : null;
    const existing =
      ptId != null
        ? prev.find((note) => Number(note?.pt_id) === ptId)
        : null;

    return {
      pt_id: ptId,
      first_name: participant?.first_name || existing?.first_name || "",
      last_name: participant?.last_name || existing?.last_name || "",
      notes: existing?.notes ?? (index === 0 ? orphanText ?? "" : ""),
    };
  });
};

/** Task indexes in the shared tasks array that belong to a participant. */
export const getTaskIndexesForParticipant = (tasks = [], participantId) => {
  const ptId =
    participantId != null && participantId !== ""
      ? Number(participantId)
      : null;
  if (ptId == null || Number.isNaN(ptId)) return [];

  const indexes = [];
  (tasks || []).forEach((task, index) => {
    if (Number(task?.pt_id) === ptId) {
      indexes.push(index);
    }
  });
  return indexes;
};

/** Tasks with no pt_id (legacy / unassigned). */
export const getUnassignedTaskIndexes = (tasks = []) => {
  const indexes = [];
  (tasks || []).forEach((task, index) => {
    if (task?.pt_id == null || task?.pt_id === "") {
      indexes.push(index);
    }
  });
  return indexes;
};

/**
 * Group shared shift-task indexes by selected participants (basic group shift UI).
 * Always returns a group per participant so empty participants can still add tasks.
 */
export const buildTaskGroupsForParticipants = (
  participants = [],
  tasks = [],
  formatName
) => {
  const list = Array.isArray(participants)
    ? participants.filter(Boolean)
    : [];

  const groups = list.map((participant) => {
    const ptId =
      participant?.id != null && participant?.id !== ""
        ? Number(participant.id)
        : null;
    const formatted =
      typeof formatName === "function"
        ? formatName(participant)
        : null;
    const name =
      (formatted && String(formatted).trim()) ||
      `${participant?.first_name || ""} ${participant?.last_name || ""}`.trim() ||
      participant?.label ||
      "Participant";

    return {
      ptId,
      participant,
      name,
      indexes: [],
    };
  });

  const unassignedIndexes = [];

  (tasks || []).forEach((task, index) => {
    const taskPtId =
      task?.pt_id != null && task?.pt_id !== "" ? Number(task.pt_id) : null;
    const group =
      taskPtId != null && !Number.isNaN(taskPtId)
        ? groups.find((item) => item.ptId === taskPtId)
        : null;
    if (group) {
      group.indexes.push(index);
    } else {
      unassignedIndexes.push(index);
    }
  });

  return { groups, unassignedIndexes };
};
